import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { MatBadgeModule } from "@angular/material/badge";
import { MatIconModule } from "@angular/material/icon";
import { MatSidenav } from "@angular/material/sidenav";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslocoModule, translate } from "@jsverse/transloco";
import { TourAnchorMatMenuDirective } from "ngx-ui-tour-md-menu";
import { Subscription, Observable, combineLatest, switchMap, map } from "rxjs";

import { LOREN_ROUTES } from "../../../../../../constants/loren-routes.constants";
import { LorenDetailsOutletService } from "../../../../../../services/loren-details-outlet-service/loren-details-outlet.service";
import { LorenNotificationService } from "../../../../../../services/loren-notification-service/loren-notification.service";
import { LorenSnackBarService } from "../../../../../../services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenSidenavTourSteps } from "../../../../../../constants/loren-tour-steps.constants";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "loren-notifications-button",
  imports: [
    CommonModule,
    TranslocoModule,
    MatTooltip,
    MatIconModule,
    MatBadgeModule,
    TourAnchorMatMenuDirective,
  ],
  templateUrl: "./loren-notifications-button.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-notifications-button.component.scss",
})
export class LorenNotificationsButtonComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private lorenDetailsOutletService = inject(LorenDetailsOutletService);
  private notificationService = inject(LorenNotificationService);
  private snackbarService = inject(LorenSnackBarService);

  private readonly destroyRef = inject(DestroyRef);
  @Input() isHandset: boolean;
  @Input() drawer: MatSidenav;

  notSeenNotifications: number;
  readonly notificationsAnchorId = LorenSidenavTourSteps.find(
    (x) => x.anchorId === "notifications"
  ).anchorId;

  private notSeenNotificationsSubscription: Subscription;

  private refreshIntervalDuration = 8 * 1000;
  private refreshInterval;

  ngOnInit() {
    this.getNotificationsSubscription();
    this.refreshInterval = window.setInterval(() => {
      this.notSeenNotificationsSubscription =
        this.getNotificationsSubscription();
    }, this.refreshIntervalDuration);
  }

  ngOnDestroy() {
    clearInterval(this.refreshInterval);
  }

  getNotificationsSubscription(): Subscription {
    return this.getNotSeenNotifications().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (value) => {
        this.notSeenNotifications = value;
        this.unsubscribeFromNotifications();
      },
      error: () => {
        this.snackbarService.showErrorMessage(
          translate("error_page.notifications_error")
        );
        this.unsubscribeFromNotifications();
        clearInterval(this.refreshInterval);
      },
    });
  }

  handleNotificationsClick() {
    const isOpen =
      this.drawer.opened &&
      this.router.url.includes(LOREN_ROUTES.NOTIFICATION_PANEL);
    this.toggleNotificationPanel(isOpen);
  }

  private toggleNotificationPanel(open: boolean) {
    this.lorenDetailsOutletService.toggleState(!open);
    if(open) {
      this.lorenDetailsOutletService.resetDetailsOutlet()
    } else {
      this.lorenDetailsOutletService.navigate(
        LOREN_ROUTES.NOTIFICATION_PANEL
      );
    }
  }

  private getNotSeenNotifications(): Observable<number> {
    return combineLatest([
      this.notificationService
        .reloadNotifications$()
        .pipe(
          switchMap(() =>
            this.notificationService.getUserNotifications()
          )
        ),
      this.notificationService.notificationStatusUpdateEvent(),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((res) => res?.length && this.prepareNotifications(res)));
  }

  private prepareNotifications(data) {
    const [allNotifications, updatedNotificationStateId] = data;

    if (updatedNotificationStateId) {
      const n = allNotifications.items?.find(
        (x) => x.id === updatedNotificationStateId
      );
      if (n) {
        n.seen = true;
      }
    }
    return allNotifications.items?.filter((n) => !n.seen)?.length;
  }

  private unsubscribeFromNotifications() {
    if (
      (this && this.notSeenNotificationsSubscription != null) ||
      this.notSeenNotificationsSubscription !== undefined
    ) {
      this.notSeenNotificationsSubscription.unsubscribe();
    }
  }
}
